import {
  AppointmentStatus,
  Gender,
  ICustomReportFilter,
  MeasureDataType,
  MeasureFormatter,
  MeasureIcon,
  MentionResourceType,
  PatientStatus,
  ReportFilterComparison,
} from '@principle-theorem/principle-core/interfaces';
import {
  GroupBy,
  IBigQueryReferrer,
  IMeasureRef,
  IPatientDimension,
  LabelAccessor,
  MeasureTransformMap,
} from '@principle-theorem/reporting/interfaces';
import {
  ISODateType,
  Timestamp,
  toInt,
  toISODate,
  toTimestamp,
} from '@principle-theorem/shared';
import { compact, first, get, isArray, isNil, isNumber } from 'lodash';
import * as moment from 'moment-timezone';
import { BigQuerySQL } from '../../big-query-sql';
import { QueryOptionResolver } from '../../query-option-resolvers';
import { getBigQueryComparison } from '../../querying';
import { BaseDimensionMeasures, latestEventId } from '../base-measures';
import { CustomReportFilter } from '../custom-report-filters';
import { DataAccessorFactory, MeasurePath } from '../data-accessor-factory';
import {
  CanBeChartedProperty,
  CanDoAllProperty,
  CanQueryByTimestampProperty,
  noFilterAccessor,
  stringPropertyHasValueFilterAccessor,
} from '../measure-properties';
import {
  defaultMetadata,
  MeasurePropertyFactory,
} from '../measure-property-factory';
import { QueryFactory } from '../query-factory';
import { AppointmentDimensionMeasureFactory } from './appointment-dimension';
import { BirthdayFilterAccessor } from './patient-birthday-filter';
import {
  PatientCombinedRecallMeasures,
  PatientRecallMeasures,
} from './patient-recall-measures';

/**
 * Sentinel value used as the age for patients without a date of birth.
 * Must be a valid non-negative number because crossfilter uses dimension
 * values as array indices internally (NaN and negative numbers crash it).
 * The labelAccessor maps this back to "No DOB" for display.
 */
export const NO_DOB_SENTINEL = 999;

export class PatientDimensionMeasures
  extends BaseDimensionMeasures
  implements MeasureTransformMap<Pick<IPatientDimension, 'status' | 'gender'>>
{
  get latestEvent(): PatientDimensionMeasures {
    const updatedAt = this.measureRef(MeasurePath.timestamp('updatedAt'));
    const measures = new PatientDimensionMeasures(this.table, this.id);
    measures.id = latestEventId(measures);
    measures._query = QueryFactory.fromTable(measures.table)
      .override(measures._query)
      .latestEvent(measures.table, GroupBy.Ref, updatedAt.attributePath)
      .get();
    return measures;
  }

  get ref(): CanBeChartedProperty {
    const docRef = this.measureRef(MeasurePath.docRef('ref'));
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix('ref'),
        label: 'Patient ID',
        isFilterable: false,
        availableAsColumn: true,
        availableAsChart: false,
        isPii: true,
        piiType: 'id',
      },
      docRef,
      this.buildQuery().attributes([docRef.attributePath]).get()
    );
  }

  get createdAt(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('createdAt'));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix('createdAt'),
        label: 'Date Patient Added',
        summary: 'Date that the patient was added to Principle',
        formatter: MeasureFormatter.Day,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get name(): CanDoAllProperty {
    const propertyName = 'name';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient Name',
        icon: MeasureIcon.Text,
        isPii: true,
        piiType: 'name',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get address(): CanDoAllProperty {
    const propertyName = 'address';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient Address',
        icon: MeasureIcon.Location,
        isPii: true,
        piiType: 'address',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get addressIsVerified(): CanDoAllProperty {
    const propertyName = 'metadata.address.isVerified';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.boolean(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient Address Is Verified',
        icon: MeasureIcon.Checkbox,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get addressPostCode(): CanDoAllProperty {
    const propertyName = 'metadata.address.components.postalCode';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient Postcode',
        icon: MeasureIcon.Location,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get addressSuburb(): CanDoAllProperty {
    const propertyName = 'metadata.address.components.suburb';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient Suburb',
        icon: MeasureIcon.Location,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get addressState(): CanDoAllProperty {
    const propertyName = 'metadata.address.components.state';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient State',
        icon: MeasureIcon.Location,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get addressCoordinates(): CanDoAllProperty {
    const propertyName = 'metadata.address.coordinates';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient Coordinates',
        icon: MeasureIcon.Location,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get dateOfBirth(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('dateOfBirth'));
    return MeasurePropertyFactory.timestamp(
      {
        id: 'patient.dateOfBirth',
        label: 'Patient Date Of Birth',
        summary: 'Date of birth of the patient',
        isPii: true,
        piiType: 'dob',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      MeasureFormatter.Day
    );
  }

  get birthday(): CanDoAllProperty {
    const measure = this.measureRef(MeasurePath.timestamp('dateOfBirth'));
    const baseMeasure = MeasurePropertyFactory.timestamp(
      {
        id: 'patient.isBirthdayToday',
        label: `Patient's Birthday`,
        summary: `Whether the date is the patient's birthday each year`,
        availableAsColumn: false,
        availableAsChart: false,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      MeasureFormatter.Day
    );
    baseMeasure.measure.filterAccessor = BirthdayFilterAccessor.filterAccessor(
      measure.factPropertyPath
    );
    return baseMeasure;
  }

  get email(): CanDoAllProperty {
    const propertyName = 'email';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient Email',
        icon: MeasureIcon.Email,
        isPii: true,
        piiType: 'email',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get mobileNumber(): CanDoAllProperty {
    const propertyName = 'mobileNumber';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient Mobile Number',
        icon: MeasureIcon.Numbers,
        isPii: true,
        piiType: 'phone',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get status(): CanDoAllProperty {
    const propertyName = 'status';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.enum<PatientStatus>(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient Status',
        summary: 'The status of the patient',
        optionResolver: QueryOptionResolver.enum(PatientStatus),
        icon: MeasureIcon.List,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      PatientStatus.Inactive
    );
  }

  get gender(): CanDoAllProperty {
    const propertyName = 'gender';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.enum<Gender>(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Patient Gender',
        summary: '',
        optionResolver: QueryOptionResolver.enum(Gender),
        icon: MeasureIcon.Person,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      Gender.Other
    );
  }

  get hasMobileNumber(): CanDoAllProperty {
    const measure = this.measureRef('mobileNumber');
    return MeasurePropertyFactory.propertyExists(
      {
        id: 'patient.hasMobileNumber',
        label: 'Patient Has Mobile Number',
        summary: 'Whether the patient has a mobile number or not',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      stringPropertyHasValueFilterAccessor(measure.factPropertyPath)
    );
  }

  get hasEmail(): CanDoAllProperty {
    const measure = this.measureRef('email');
    return MeasurePropertyFactory.propertyExists(
      {
        id: 'patient.hasEmail',
        label: 'Patient Has Email',
        summary: 'Whether the patient has an email address or not',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      stringPropertyHasValueFilterAccessor(measure.factPropertyPath)
    );
  }

  get hasOptedInToMarketingCampaigns(): CanDoAllProperty {
    const measure = this.measureRef('metadata.receiveMarketing');
    return MeasurePropertyFactory.boolean(
      {
        id: 'patient.metadata.receiveMarketing',
        label: 'Patient Receives Marketing',
        summary: 'Whether the patient has opted in to Marketing Campaigns',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      false,
      true
    );
  }

  get distance(): CanBeChartedProperty {
    const patientCoordinates = this.measureRef('metadata.address.coordinates');
    const practiceCoordinates: IMeasureRef = {
      attributePath: 'practice.address.coordinates',
      factPropertyPath: 'practice_coordinates',
    };

    return new CanBeChartedProperty({
      metadata: defaultMetadata({
        id: this._pathWithPrefix('distance'),
        label: 'Patient Distance',
        summary: 'Distance between the patient and the practice in KMs',
        dataType: MeasureDataType.Number,
        formatter: MeasureFormatter.Suffix,
        formatterValue: 'kms',
      }),
      measure: {
        propertyName: patientCoordinates.attributePath,
        query: this.buildQuery()
          .attributes([
            patientCoordinates.attributePath,
            practiceCoordinates.attributePath,
          ])
          .get(),
        dataAccessor: DataAccessorFactory.distance(
          patientCoordinates.factPropertyPath,
          practiceCoordinates.factPropertyPath
        ),
        filterAccessor: noFilterAccessor(),
      },
    });
  }

  get age(): CanDoAllProperty {
    const measure = this.measureRef(MeasurePath.timestamp('dateOfBirth'));
    return new CanDoAllProperty({
      metadata: defaultMetadata({
        id: this._pathWithPrefix('age'),
        label: 'Patient Age',
        summary: 'Age of the patient in years',
        dataType: MeasureDataType.Number,
        formatter: MeasureFormatter.Suffix,
        formatterValue: 'years',
        icon: MeasureIcon.Numbers,
      }),
      measure: {
        propertyName: measure.attributePath,
        query: this.buildQuery().attributes([measure.attributePath]).get(),
        dataAccessor: (fact) =>
          this._buildAge(measure.factPropertyPath, fact) ?? NO_DOB_SENTINEL,
        labelAccessor: (fact) => {
          const age = this._buildAge(measure.factPropertyPath, fact);
          return age !== undefined ? age.toString() : 'No DOB';
        },
        filterAccessor: (filter) => {
          const values = filter.values.map((filterValue) =>
            isNil(filterValue.value)
              ? undefined
              : isNumber(filterValue.value)
                ? filterValue.value
                : toInt(filterValue.value)
          );
          const value = first(values);
          // Use strict undefined check — `!value` would also drop age 0.
          // Also guard against NaN from malformed numeric strings.
          if (value === undefined || isNaN(value)) {
            return;
          }
          // The NO_DOB_SENTINEL flows from the dataAccessor when a patient
          // has no DOB. Filter for NULL dateOfBirth in BigQuery instead of
          // generating a nonsensical "999 years ago" timestamp comparison.
          if (value === NO_DOB_SENTINEL) {
            return BigQuerySQL.filter(
              measure.factPropertyPath,
              'IS',
              undefined
            );
          }
          return getAgeFilter(
            measure.factPropertyPath,
            value,
            filter.comparison
          );
        },
      },
      groupMeasure: {
        queryAttributes: [measure.attributePath],
        dataAccessor: (fact) =>
          this._buildAge(measure.factPropertyPath, fact) ?? NO_DOB_SENTINEL,
        labelAccessor: (fact) => {
          const age = this._buildAge(measure.factPropertyPath, fact);
          return age !== undefined ? age.toString() : 'No DOB';
        },
      },
    });
  }

  get tags(): CanDoAllProperty {
    const measure = this.measureRef('tags');
    return MeasurePropertyFactory.arrayRef(
      {
        id: this._pathWithPrefix('tags'),
        label: 'Patient Tags',
        summary: 'Tags that have ben added to the patient',
        dataType: MeasureDataType.String,
        formatter: MeasureFormatter.Text,
        optionResolver: QueryOptionResolver.patientTags(),
        icon: MeasureIcon.Tag,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get ageRange(): CanDoAllProperty {
    const measure = this.measureRef(MeasurePath.timestamp('dateOfBirth'));
    return new CanDoAllProperty({
      metadata: defaultMetadata({
        id: this._pathWithPrefix('ageRange'),
        label: 'Patient Age Range',
        summary: '',
        dataType: MeasureDataType.Number,
        formatter: MeasureFormatter.Suffix,
        formatterValue: 'years',
        isFilterable: false,
      }),
      measure: {
        propertyName: measure.attributePath,
        query: this.buildQuery().attributes([measure.attributePath]).get(),
        dataAccessor: (fact) => {
          const age = this._buildAge(measure.factPropertyPath, fact);
          return getAgeRangeIndex(age);
        },
        filterAccessor: (filter) => {
          const ageFilters = filter.values.map((filterValue) => {
            if (isNil(filterValue.value)) {
              return;
            }

            const value = isNumber(filterValue.value)
              ? filterValue.value
              : toInt(filterValue.value);

            return getAgeRangeToFilter(measure.factPropertyPath, value);
          });
          return BigQuerySQL.or(compact(ageFilters));
        },
      },
      groupMeasure: {
        queryAttributes: [measure.attributePath],
        dataAccessor: (fact) => {
          const age = this._buildAge(measure.factPropertyPath, fact);
          return getAgeRangeIndex(age);
        },
        labelAccessor: (fact) => {
          const age = this._buildAge(measure.factPropertyPath, fact);
          return getAgeRangeLabel(getAgeRangeIndex(age));
        },
      },
    });
  }

  get referrer(): CanDoAllProperty {
    const name = this.measureRef('referrer.name');
    const docRef = this.measureRef(MeasurePath.docRef('referrer.ref'));
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix('referrer'),
        label: 'Referrer Name',
        summary: `The name of the referral source of the patient. If the patient was referred by another patient, the patient's name will be shown`,
        icon: MeasureIcon.Referral,
        optionResolver: QueryOptionResolver.referrerNames(),
      },
      docRef,
      this.buildQuery()
        .attributes([docRef.attributePath, name.attributePath])
        .get(),
      name,
      'None'
    );
  }

  get referrerType(): CanDoAllProperty {
    const defaultValue = 'None';

    const referrerRef = this.measureRef(MeasurePath.docRef('referrer.ref'));

    const referrerType = this.measureRef('referrer.type');
    const referrerTypeAccessor = DataAccessorFactory.property<string>(
      referrerType.factPropertyPath,
      defaultValue
    );

    const referrerName = this.measureRef('referrer.name');
    const referrerNameAccessor = DataAccessorFactory.property<string>(
      referrerName.factPropertyPath,
      defaultValue
    );

    const base = MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix('referrerType'),
        label: 'Referrer Type',
        summary:
          'The type of the referral source of the patient. If the patient was referred by another patient, the type will be "Patient"',
        icon: MeasureIcon.Referral,
        optionResolver: QueryOptionResolver.referrerTypes(),
      },
      referrerRef,
      this.buildQuery()
        .attributes([
          referrerType.attributePath,
          referrerName.attributePath,
          referrerRef.attributePath,
        ])
        .get(),
      referrerName,
      defaultValue
    );

    const labelAccessor: LabelAccessor = (fact: unknown) => {
      const factReferrerType = referrerTypeAccessor(fact);
      if (factReferrerType === MentionResourceType.Patient) {
        return 'Patient';
      }
      return referrerNameAccessor(fact);
    };

    const filterAccessor = (filter: ICustomReportFilter): string => {
      const filters = filter.values.map((filterValue) => {
        if (isArray(filterValue.value)) {
          return;
        }
        const comparison = CustomReportFilter.getComparison(filter);
        const bigQueryComparison = getBigQueryComparison(filter, comparison);

        const dataPropertyPath =
          filterValue.value === MentionResourceType.Patient
            ? referrerType.factPropertyPath
            : referrerRef.factPropertyPath;

        const filterResult = BigQuerySQL.filter(
          dataPropertyPath,
          bigQueryComparison,
          filterValue.value
        );

        return filterResult;
      });

      return BigQuerySQL.or(compact(filters));
    };
    base.measure.filterAccessor = filterAccessor;
    base.measure.sortAccessor = labelAccessor;
    base.measure.labelAccessor = labelAccessor;
    base.groupMeasure.labelAccessor = labelAccessor;
    return base;
  }

  get patientReferrers(): CanDoAllProperty {
    const referrerMeasure = this.measureRef('referrer');
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix('patientReferrers'),
        label: 'Patient Referrers',
        summary:
          'The names of the patients who referred the patient. If they were not referred by a patient, "None" will be shown.',
      },
      referrerMeasure,
      this.buildQuery().attributes([referrerMeasure.attributePath]).get(),
      'None',
      (fact: unknown) => {
        const referrer = get(
          fact,
          referrerMeasure.factPropertyPath,
          undefined
        ) as IBigQueryReferrer | undefined;

        if (!referrer || referrer.type !== MentionResourceType.Patient) {
          return 'None';
        }

        return referrer.name;
      }
    );
  }

  get preferredFeeSchedule(): CanDoAllProperty {
    const propertyName = 'preferredFeeSchedule';
    const docRef = this.measureRef(MeasurePath.docRef(`${propertyName}.ref`));
    const name = this.measureRef(`${propertyName}.name`);
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Preferred Fee Schedule',
        summary:
          'The name of the fee schedule that has been set for this patient',
        dataType: MeasureDataType.String,
        optionResolver: QueryOptionResolver.feeSchedules(),
        icon: MeasureIcon.List,
      },
      docRef,
      this.buildQuery()
        .attributes([docRef.attributePath, name.attributePath])
        .get(),
      name,
      'None'
    );
  }

  get preferredDentist(): CanDoAllProperty {
    const propertyName = 'preferredDentist';
    const docRef = this.measureRef(MeasurePath.docRef(`${propertyName}.ref`));
    const name = this.measureRef(`${propertyName}.name`);
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Preferred Dentist',
        summary: 'The name of the dentist that the patient prefers to see',
        dataType: MeasureDataType.String,
        optionResolver: QueryOptionResolver.staff(),
        icon: MeasureIcon.Person,
      },
      docRef,
      this.buildQuery()
        .attributes([docRef.attributePath, name.attributePath])
        .get(),
      name,
      'None'
    );
  }

  get preferredHygienist(): CanDoAllProperty {
    const propertyName = 'preferredHygienist';
    const docRef = this.measureRef(MeasurePath.docRef(`${propertyName}.ref`));
    const name = this.measureRef(`${propertyName}.name`);
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Preferred Hygienist',
        summary: 'The name of the hygienist that the patient prefers to see',
        optionResolver: QueryOptionResolver.staff(),
        icon: MeasureIcon.Person,
      },
      docRef,
      this.buildQuery()
        .attributes([docRef.attributePath, name.attributePath])
        .get(),
      name,
      'None'
    );
  }

  get preferredPractice(): CanDoAllProperty {
    const propertyName = 'preferredPractice';
    const docRef = this.measureRef(MeasurePath.docRef(`${propertyName}.ref`));
    const name = this.measureRef(`${propertyName}.name`);
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Preferred Practice',
        summary: 'The name of the practice that the patient prefers to visit',
        optionResolver: QueryOptionResolver.practices(),
        icon: MeasureIcon.Practice,
      },
      docRef,
      this.buildQuery()
        .attributes([docRef.attributePath, name.attributePath])
        .get(),
      name,
      'None'
    );
  }

  get depositsPaid(): CanBeChartedProperty {
    const propertyName = 'accountSummary.creditSummary.depositsPaid';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Deposits Paid',
        summary: 'Total of all deposits ever paid by the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get creditTotal(): CanBeChartedProperty {
    const propertyName = 'accountSummary.creditSummary.creditTotal';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Account Credit Total',
        summary:
          'Total of all account credits the patient has ever amassed, regardless of how much has been used.',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get creditUsed(): CanBeChartedProperty {
    const propertyName = 'accountSummary.creditSummary.creditUsed';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Account Credit Used',
        summary: 'Total of all account credits ever used by the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      0,
      (value) => value * -1
    );
  }

  get creditRemaining(): CanBeChartedProperty {
    const propertyName = 'accountSummary.creditSummary.creditRemaining';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Account Credit Remaining',
        summary: 'Total of all unused account credits available to the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get treatmentsInvoiced(): CanBeChartedProperty {
    const propertyName = 'accountSummary.invoiceSummary.treatments';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Treatments Invoiced',
        summary: 'Total of all treatments ever invoiced to the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get productsInvoiced(): CanBeChartedProperty {
    const propertyName = 'accountSummary.invoiceSummary.products';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Products Invoiced',
        summary: 'Total of all products ever invoiced to the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get feesInvoiced(): CanBeChartedProperty {
    const propertyName = 'accountSummary.invoiceSummary.fees';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Fees Invoiced',
        summary: 'Total of all fees ever invoiced to the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get subtotalInvoiced(): CanBeChartedProperty {
    const propertyName = 'accountSummary.invoiceSummary.subtotal';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Subtotal Invoiced',
        summary:
          'Subtotal of all invoices ever issued to the patient, excluding tax.',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get depositsInvoiced(): CanBeChartedProperty {
    const propertyName = 'accountSummary.invoiceSummary.deposits';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Deposits Invoiced',
        summary: 'Total of all deposits ever invoiced to the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get totalInvoiced(): CanBeChartedProperty {
    const propertyName = 'accountSummary.invoiceSummary.totalInvoiced';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Total Invoiced',
        summary:
          'Total of all invoices ever issued to the patient, including tax.',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get discountsGiven(): CanBeChartedProperty {
    const propertyName = 'accountSummary.paymentSummary.discounts';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Total Discounts Given to Patient',
        summary: 'Total of all discounts ever given to the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      0,
      (value) => value * -1
    );
  }

  get writtenOff(): CanBeChartedProperty {
    const propertyName = 'accountSummary.paymentSummary.writtenOff';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Total Written Off',
        summary:
          'Total of the unpaid remainder of invoices ever written off for the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      0,
      (value) => value * -1
    );
  }

  get creditsUsed(): CanBeChartedProperty {
    const propertyName = 'accountSummary.paymentSummary.creditUsed';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Total Credits Used',
        summary: 'Total of all account credits ever used by the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      0,
      (value) => value * -1
    );
  }

  get totalReceivable(): CanBeChartedProperty {
    const propertyName = 'accountSummary.paymentSummary.totalReceivable';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Total Receivable',
        summary:
          'Total of all of the patients invoices, minus discounts, minus written off amounts, minus account credits used.',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get paymentsReceived(): CanBeChartedProperty {
    const propertyName = 'accountSummary.paymentSummary.paymentsReceived';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Total Payments Received',
        summary:
          'Total of all of the patients payments received, excluding discounts & account credits.',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get outstanding(): CanBeChartedProperty {
    const propertyName = 'accountSummary.paymentSummary.outstanding';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: propertyName,
        label: 'Total Outstanding',
        summary:
          'Total of all outstanding amounts from invoices for the patient',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get lastVisitTreatmentCategory(): CanDoAllProperty {
    const treatmentCategory =
      'lastVisit.treatmentPlan.treatmentStep.treatmentCategory';
    const labelMeasure = this.measureRef(`${treatmentCategory}.name`);
    const colourMeasure = this.measureRef(`${treatmentCategory}.colour.value`);
    const dataMeasure = this.measureRef(
      MeasurePath.docRef(`${treatmentCategory}.ref`)
    );

    return AppointmentDimensionMeasureFactory.treatmentCategory(
      {
        id: 'lastVisit.treatmentCategory',
        label: 'Last Visit Treatment Category',
        summary: `The primary treatment category of the patient's last visit`,
        optionResolver: QueryOptionResolver.treatmentCategories(),
        icon: MeasureIcon.List,
      },
      labelMeasure,
      colourMeasure,
      dataMeasure,
      this.buildQuery()
        .attributes([
          labelMeasure.attributePath,
          colourMeasure.attributePath,
          dataMeasure.attributePath,
        ])
        .get()
    );
  }

  get lastVisitPractitioner(): CanDoAllProperty {
    const name = this.measureRef('lastVisit.practitioner.name');
    const docRef = this.measureRef(
      MeasurePath.docRef('lastVisit.practitioner.ref')
    );
    return MeasurePropertyFactory.docRef(
      {
        id: 'lastVisit.practitioner',
        label: 'Last Visit Practitioner',
        summary: 'The name of the practitioner that the patient last saw',
        optionResolver: QueryOptionResolver.staff(),
        icon: MeasureIcon.Person,
      },
      docRef,
      this.buildQuery()
        .attributes([name.attributePath, docRef.attributePath])
        .get(),
      name
    );
  }

  get lastVisitPractice(): CanDoAllProperty {
    const name = this.measureRef('lastVisit.practice.name');
    const docRef = this.measureRef(
      MeasurePath.docRef('lastVisit.practice.ref')
    );
    return MeasurePropertyFactory.docRef(
      {
        id: 'lastVisit.practice',
        label: 'Last Visit Practice',
        summary: 'The name of the practice that the patient last visited',
        optionResolver: QueryOptionResolver.practices(),
        icon: MeasureIcon.Practice,
      },
      docRef,
      this.buildQuery()
        .attributes([name.attributePath, docRef.attributePath])
        .get(),
      name
    );
  }

  get hasHadAppointment(): CanDoAllProperty {
    const measure = this.measureRef(MeasurePath.timestamp('lastVisit.from'));
    return MeasurePropertyFactory.propertyExists(
      {
        id: 'hasHadAppointment',
        label: 'Has Had Appointment',
        summary: 'Whether the patient has had at least one appointment',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get lastVisitDate(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('lastVisit.from'));
    return MeasurePropertyFactory.timestamp(
      {
        id: 'lastVisit.date',
        label: 'Last Visit Date',
        summary: 'Date that the patient last had an appointment',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      MeasureFormatter.Day
    );
  }

  get lastVisitTags(): CanDoAllProperty {
    const measure = this.measureRef('lastVisit.tags');
    return MeasurePropertyFactory.arrayRef(
      {
        id: 'lastVisit.tags',
        label: 'Last Visit Tags',
        summary: `The appointment tags on the patient's last visit`,
        dataType: MeasureDataType.String,
        formatter: MeasureFormatter.Text,
        optionResolver: QueryOptionResolver.appointmentTags(),
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get lastVisitStatus(): CanDoAllProperty {
    const measure = this.measureRef('lastVisit.status');
    return MeasurePropertyFactory.enum<AppointmentStatus>(
      {
        id: 'lastVisit.status',
        label: 'Last Visit Status',
        summary: `The status of the patient's last visit`,
        optionResolver: QueryOptionResolver.enum(AppointmentStatus),
        icon: MeasureIcon.List,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      'N/A'
    );
  }

  get nextVisitBooked(): CanDoAllProperty {
    const measure = this.measureRef('nextVisit');
    return MeasurePropertyFactory.propertyExists(
      {
        id: 'nextVisit.isBooked',
        label: 'Next Visit Booked',
        summary: 'Whether the patient has a next visit booked or not',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get nextVisitTreatmentCategory(): CanDoAllProperty {
    const treatmentCategory =
      'nextVisit.treatmentPlan.treatmentStep.treatmentCategory';
    const labelMeasure = this.measureRef(`${treatmentCategory}.name`);
    const colourMeasure = this.measureRef(`${treatmentCategory}.colour.value`);
    const dataMeasure = this.measureRef(
      MeasurePath.docRef(`${treatmentCategory}.ref`)
    );

    return AppointmentDimensionMeasureFactory.treatmentCategory(
      {
        id: 'nextVisit.treatmentCategory',
        label: 'Next Visit Treatment Category',
        summary: `The primary treatment category of the patient's next visit`,
        optionResolver: QueryOptionResolver.treatmentCategories(),
        icon: MeasureIcon.List,
      },
      labelMeasure,
      colourMeasure,
      dataMeasure,
      this.buildQuery()
        .attributes([
          labelMeasure.attributePath,
          colourMeasure.attributePath,
          dataMeasure.attributePath,
        ])
        .get()
    );
  }

  get nextVisitPractitioner(): CanDoAllProperty {
    const name = this.measureRef('nextVisit.practitioner.name');
    const docRef = this.measureRef(
      MeasurePath.docRef('nextVisit.practitioner.ref')
    );
    return MeasurePropertyFactory.docRef(
      {
        id: 'nextVisit.practitioner',
        label: 'Next Visit Practitioner',
        summary:
          'The name of the practitioner that the patient is booked to see',
        optionResolver: QueryOptionResolver.staff(),
        icon: MeasureIcon.Person,
      },
      docRef,
      this.buildQuery()
        .attributes([name.attributePath, docRef.attributePath])
        .get(),
      name
    );
  }

  get nextVisitPractice(): CanDoAllProperty {
    const name = this.measureRef('nextVisit.practice.name');
    const docRef = this.measureRef(
      MeasurePath.docRef('nextVisit.practice.ref')
    );
    return MeasurePropertyFactory.docRef(
      {
        id: 'nextVisit.practice',
        label: 'Next Visit Practice',
        summary: 'The name of the practice that the patient is booked to visit',
        optionResolver: QueryOptionResolver.practices(),
        icon: MeasureIcon.Practice,
      },
      docRef,
      this.buildQuery()
        .attributes([name.attributePath, docRef.attributePath])
        .get(),
      name
    );
  }

  get nextVisitDate(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('nextVisit.from'));
    return MeasurePropertyFactory.timestamp(
      {
        id: 'nextVisit.date',
        label: 'Next Visit Date',
        summary: `Date of the patient's next appointment`,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      MeasureFormatter.Day
    );
  }

  get nextVisitTags(): CanDoAllProperty {
    const measure = this.measureRef('nextVisit.tags');
    return MeasurePropertyFactory.arrayRef(
      {
        id: 'nextVisit.tags',
        label: 'Next Visit Tags',
        summary: `The appointment tags on the patient's next visit`,
        dataType: MeasureDataType.String,
        formatter: MeasureFormatter.Text,
        optionResolver: QueryOptionResolver.appointmentTags(),
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get nextVisitStatus(): CanDoAllProperty {
    const measure = this.measureRef('nextVisit.status');
    return MeasurePropertyFactory.enum<AppointmentStatus>(
      {
        id: 'nextVisit.status',
        label: 'Next Visit Status',
        summary: `The status of the patient's next visit`,
        optionResolver: QueryOptionResolver.enum(AppointmentStatus, 'N/A'),
        icon: MeasureIcon.List,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      'N/A'
    );
  }

  get hasMedicareCard(): CanDoAllProperty {
    const measure = this.measureRef('medicareCard.number');
    return MeasurePropertyFactory.propertyExists(
      {
        id: 'patient.hasMedicareCard',
        label: 'Patient Has Medicare Card',
        summary: 'Whether the patient has a Medicare Card or not',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      stringPropertyHasValueFilterAccessor(measure.factPropertyPath)
    );
  }

  get hasDvaCard(): CanDoAllProperty {
    const measure = this.measureRef('dvaCard.number');
    return MeasurePropertyFactory.propertyExists(
      {
        id: 'patient.hasDvaCard',
        label: 'Patient Has DVA Card',
        summary: 'Whether the patient has a DVA Card or not',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      stringPropertyHasValueFilterAccessor(measure.factPropertyPath)
    );
  }

  get hasHealthFundCard(): CanDoAllProperty {
    const measure = this.measureRef('healthFundCard.membershipNumber');
    return MeasurePropertyFactory.propertyExists(
      {
        id: 'patient.hasHealthFundCard',
        label: 'Patient Has Health Fund Card',
        summary: 'Whether the patient has a Health Fund Card or not',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      stringPropertyHasValueFilterAccessor(measure.factPropertyPath)
    );
  }

  get healthFundType(): CanDoAllProperty {
    const measure = this.measureRef('healthFundCard.fundCode');
    return MeasurePropertyFactory.string(
      {
        id: 'patient.healthFundType',
        label: 'Patient Health Fund Type',
        summary: `The type of health fund that the patients is a member of`,
        icon: MeasureIcon.CreditCard,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      'No Health Fund Type'
    );
  }

  get recall(): PatientRecallMeasures {
    return new PatientRecallMeasures(this, 'recall', 'Recall');
  }

  get secondaryRecall(): PatientRecallMeasures {
    return new PatientRecallMeasures(
      this,
      'secondaryRecall',
      'Secondary Recall',
    );
  }

  get combinedRecall(): PatientCombinedRecallMeasures {
    return new PatientCombinedRecallMeasures(this);
  }

  /**
   * Returns undefined (not 0) when the patient has no DOB, so callers
   * can distinguish "no data" from "genuinely 0 years old".
   */
  private _buildAge(propertyName: string, fact: unknown): number | undefined {
    const dateOfBirth = DataAccessorFactory.timestamp(
      propertyName,
      undefined
    )(fact);
    return dateOfBirth
      ? moment().diff(moment(dateOfBirth), 'years')
      : undefined;
  }
}

export function getAgeRangeLabel(index: AgeRange): string {
  switch (index) {
    case AgeRange.NoDob:
      return 'No DOB';
    case AgeRange.Under12:
      return 'Under 12';
    case AgeRange.Between12And17:
      return 'Between 12 And 17';
    case AgeRange.Between18And24:
      return 'Between 18 And 24';
    case AgeRange.Between25And34:
      return 'Between 25 And 34';
    case AgeRange.Between35And44:
      return 'Between 35 And 44';
    case AgeRange.Between45And54:
      return 'Between 45 And 54';
    case AgeRange.Between55And64:
      return 'Between 55 And 64';
    case AgeRange.Between65And74:
      return 'Between 65 And 74';
    case AgeRange.OlderThan74:
      return 'Older Than 74';
    default:
      return '';
  }
}

export function getAgeRangeIndex(age: number | undefined): AgeRange {
  if (age === undefined) {
    return AgeRange.NoDob;
  }
  if (age < 12) {
    return AgeRange.Under12;
  }
  if (age < 18) {
    return AgeRange.Between12And17;
  }
  if (age < 25) {
    return AgeRange.Between18And24;
  }
  if (age < 35) {
    return AgeRange.Between25And34;
  }
  if (age < 45) {
    return AgeRange.Between35And44;
  }
  if (age < 55) {
    return AgeRange.Between45And54;
  }
  if (age < 65) {
    return AgeRange.Between55And64;
  }
  if (age < 75) {
    return AgeRange.Between65And74;
  }
  return AgeRange.OlderThan74;
}

export function getAgeRangeFilter(
  path: string,
  minAge: number = 0,
  maxAge: number = 150
): string {
  return BigQuerySQL.dateRange(path, getYearsAgo(maxAge), getYearsAgo(minAge));
}

export function getAgeFilter(
  path: string,
  age: number,
  comparison?: ReportFilterComparison
): string {
  const yearsAgo = getYearsAgoTimestamp(age);
  const yearsAgoPlusOne = getYearsAgoTimestamp(age + 1);

  switch (comparison) {
    case ReportFilterComparison.GreaterThanOrEqualTo:
      return BigQuerySQL.timestamp(path, '<=', yearsAgo);
    case ReportFilterComparison.LessThanOrEqualTo:
      return BigQuerySQL.timestamp(path, '>', yearsAgoPlusOne);
    case ReportFilterComparison.GreaterThan:
      return BigQuerySQL.timestamp(path, '<=', yearsAgoPlusOne);
    case ReportFilterComparison.LessThan:
      return BigQuerySQL.timestamp(path, '>', yearsAgo);
    case ReportFilterComparison.IsEqualTo:
      return BigQuerySQL.and([
        BigQuerySQL.timestamp(path, '>', yearsAgoPlusOne),
        BigQuerySQL.timestamp(path, '<=', yearsAgo),
      ]);
    default:
      return '1=1';
  }
}

export function getAgeRangeToFilter(
  path: string,
  ageRange: AgeRange
): string | undefined {
  switch (ageRange) {
    // Patients with no DOB have NULL dateOfBirth in BigQuery
    case AgeRange.NoDob:
      return BigQuerySQL.filter(path, 'IS', undefined);
    case AgeRange.Under12:
      return BigQuerySQL.filter(path, '>=', getYearsAgo(12));
    case AgeRange.Between12And17:
      return getAgeRangeFilter(path, 12, 17);
    case AgeRange.Between18And24:
      return getAgeRangeFilter(path, 17, 24);
    case AgeRange.Between25And34:
      return getAgeRangeFilter(path, 24, 34);
    case AgeRange.Between35And44:
      return getAgeRangeFilter(path, 34, 44);
    case AgeRange.Between45And54:
      return getAgeRangeFilter(path, 44, 54);
    case AgeRange.Between55And64:
      return getAgeRangeFilter(path, 54, 64);
    case AgeRange.Between65And74:
      return getAgeRangeFilter(path, 64, 74);
    case AgeRange.OlderThan74:
      return BigQuerySQL.filter(path, '<=', getYearsAgo(74));
    default:
      return undefined;
  }
}

function getYearsAgo(numYears: number): ISODateType {
  return toISODate(moment().subtract(numYears, 'years'));
}

function getYearsAgoTimestamp(numYears: number): Timestamp {
  return toTimestamp(moment().subtract(numYears, 'years'));
}

/**
 * Enum values are used as crossfilter array indices, so they must be
 * non-negative integers. NoDob is placed last (9) for this reason.
 */
export enum AgeRange {
  Under12 = 0,
  Between12And17 = 1,
  Between18And24 = 2,
  Between25And34 = 3,
  Between35And44 = 4,
  Between45And54 = 5,
  Between55And64 = 6,
  Between65And74 = 7,
  OlderThan74 = 8,
  NoDob = 9,
}
